<script>
  import { onMount, tick } from "svelte";
  import Scroller from "./vendor/Scroller.svelte";
  import DOMPurify from "dompurify";

  export let scrollyId; // Passed from the parent
  export let externalData; // Passed from the parent

  let index; // int index of currently active foreground DOM element
  let offset; // float offset of currently active foreground DOM element - 0 to 1 value
  let progress; // float how far along the whole scrolly we currently are - 0 to 1 value
  let count; // int total num steps (=DOM elements) of the foreground
  let top = 0;
  let bottom = 1;
  let threshold = 0.5;

  let prevOffsets = []; // Store previous offsets for each index
  let currentIndex = -1; // Track the currently active index
  let scrollerEntered = false;
  let firstSlideEntered = false;
  let lastSlideEntered = false;
  let triggeredStates = [];

  let scrollDirection = "down";
  var enterScrolly = false;
  var exitScrolly = false;
  let prevOffsetScrolly;

  let aspectRatioSVG = null;
  let scrollyContainer;
  let scrollyMarginTop = "0px";
  let scrollyMarginBottom = "0px";

  for (let i = 0; i < count; i++) {
    prevOffsets[i] = 0;
  }
  // Reference to the background container element.
  let backgroundContainer;
  let foregroundContainer;

  function executeScriptsInContainer(container) {
    if (!container) return;

    const scripts = container.querySelectorAll("script");
    scripts.forEach((oldScript) => {
      if (oldScript.dataset.executed === "true") return;

      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name !== "data-executed") {
          newScript.setAttribute(attr.name, attr.value);
        }
      });
      newScript.textContent = oldScript.textContent;
      newScript.dataset.executed = "true";
      oldScript.parentNode.removeChild(oldScript);
      container.appendChild(newScript);
    });
  }

  // On mount, extract any script tags from the inserted HTML and reinsert them so they execute.
  onMount(async () => {
    await tick(); // Wait for DOM updates to complete
    if (backgroundContainer) {
      prevOffsets = new Array(count).fill(0);
      executeScriptsInContainer(backgroundContainer);
    }
    executeScriptsInContainer(foregroundContainer);

    const hash = window.location.hash;
    if (!hash) return;
    const targetId = hash.slice(1);
    const interval = setInterval(() => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", inline: "center" });
        clearInterval(interval);
      }
      //console.log("hello hash#3 ", hash);
    }, 1000);
  });

  function updateScrollyMargins() {
    if (!scrollyContainer) return;

    const iframe = document.querySelector(`iframe[data-scrolly="${scrollyId}"]`);
    if (!(iframe instanceof HTMLIFrameElement) || !aspectRatioSVG) {
      scrollyMarginTop = "0px";
      scrollyMarginBottom = "0px";
      return;
    }

    const widthPx = iframe.offsetWidth;
    const heightPx = iframe.offsetHeight;
    const baseHeight = widthPx / aspectRatioSVG;
    const tempMargin = Math.max(0, (heightPx - baseHeight) / 2);
    const headerMargin = 58;

    scrollyMarginTop = `${-(tempMargin + headerMargin)}px`;
    scrollyMarginBottom = `${-tempMargin}px`;
  }

  const triggeredThresholdsMap = new Map();

  $: {
    // Determine scroll direction
    scrollDirection = offset > prevOffsetScrolly ? "down" : offset < prevOffsetScrolly ? "up" : scrollDirection;

    //let iframe = document.querySelector("iframe");
    const iframe = document.querySelector('iframe[data-scrolly="' + scrollyId + '"]');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.contentWindow.postMessage({ type: "myprogress", value: progress, id: scrollyId }, "*");
      iframe.contentWindow.postMessage(
        { type: "currentprogress", value: prevOffsets[currentIndex], id: scrollyId },
        "*",
      );
    }
    if (externalData) {
      externalData.foregroundContent.forEach((section, idx) => {
        const isFirstSlide = idx === 0;
        const isLastSlide = idx === count - 1;

        // Initialisiere den Schwellenwert-Zustand für diese Folie, falls noch nicht vorhanden
        if (!triggeredThresholdsMap.has(idx)) {
          triggeredThresholdsMap.set(
            idx,
            section.thresholds.map((threshold) => ({
              value: threshold.value,
              lastTriggeredDirection: null, // Anfangszustand: noch nicht ausgelöst
            })),
          );
        }

        const thresholdsState = triggeredThresholdsMap.get(idx);
        // Handle entering the first slide
        if (isFirstSlide && prevOffsets[0] > 0 && !enterScrolly) {
          enterScrolly = true;
          triggerActions(section.onEnter, "enter", iframe, scrollyId);
        }

        // Handle exiting the first slide
        if (isFirstSlide && prevOffsets[0] < 0 && enterScrolly) {
          enterScrolly = false;
          triggerActions(section.onExit, "exit", iframe, scrollyId);
        }

        // Handle entering the last slide
        if (isLastSlide && prevOffsets[count - 1] < 1 && exitScrolly) {
          exitScrolly = false;
          triggerActions(section.onEnter, "enter", iframe, scrollyId);
        }

        // Handle exiting the last slide
        if (isLastSlide && prevOffsets[count - 1] > 1 && !exitScrolly) {
          exitScrolly = true;
          triggerActions(section.onExit, "exit", iframe, scrollyId);
        }

        // Handle entering a new slide
        if (idx === index && currentIndex !== idx) {
          triggerActions(section.onEnter, "enter", iframe, scrollyId);
        }

        // Handle exiting the current slide
        if (idx === currentIndex && idx !== index) {
          triggerActions(section.onExit, "exit", iframe, scrollyId);
        }

        // Handle thresholds within the slide using slide-specific offset
        const slideOffset = prevOffsets[idx]; // Use slide-specific offset
        const slideProgress = slideOffset; // Convert to decimal for percentage comparison

        // Handle thresholds within the slide
        // Verarbeite Schwellenwerte
        thresholdsState.forEach((thresholdState) => {
          const thresholdValue = thresholdState.value / 100; // Prozentwert in Dezimal umwandeln
          if (slideProgress >= thresholdValue && thresholdState.lastTriggeredDirection !== "down") {
            // Runterscrollen: Schwellenwert überschritten
            thresholdState.lastTriggeredDirection = "down"; // Markiere Richtung als "down"
            const threshold = section.thresholds.find((t) => t.value === thresholdState.value);
            //console.log(`Triggering threshold ${thresholdState.value}% on slide ${idx} (down)`, threshold.actions);

            triggerActions(threshold.actions, `threshold-${thresholdState.value}-down`, iframe, scrollyId);
          } else if (slideProgress < thresholdValue && thresholdState.lastTriggeredDirection !== "up") {
            // Hochscrollen: Zurückkehren unter den Schwellenwert
            thresholdState.lastTriggeredDirection = "up"; // Markiere Richtung als "up"
            const threshold = section.thresholds.find((t) => t.value === thresholdState.value);
            //console.log(`Triggering threshold ${thresholdState.value}% on slide ${idx} (up)`, threshold.actions);
            triggerActions(threshold.actions, `threshold-${thresholdState.value}-up`, iframe, scrollyId);
          }
        });
      });
    }

    // Update currentIndex and prevOffsets
    if (currentIndex !== index) {
      currentIndex = index;
    }
    if (index >= 0) {
      prevOffsets[index] = offset;
    }
    prevOffsetScrolly = offset;
  }

  // Function to trigger actions
  function triggerActions(actions, transitionType, iframe, indexIframe) {
    if (actions && actions.length > 0) {
      actions.forEach((action) => {
        // console.log(
        //   `xxxxxxxxxxxxxxxxxx ${transitionType} action triggered:`,
        //   iframe,
        //   action.method,
        //   action.args,
        //   transitionType,
        // );
        iframe.contentWindow.postMessage(
          {
            action: action.method,
            params: action.args,
            id: indexIframe,
          },
          "*",
        );
      });
    } else {
      //console.log(`No actions to trigger for ${transitionType}.`);
    }
  }

  function handleThresholds(thresholds, slideProgress, scrollDirection, iframe, indexIframe, slideIndex) {
    const triggeredThresholds = triggeredThresholdsMap.get(slideIndex);
    if (scrollDirection === "down") {
      thresholds.sort((a, b) => a.value - b.value); // Sort thresholds from lowest to highest
      thresholds.forEach((threshold) => {
        const thresholdValue = threshold.value / 100; // Convert threshold value to decimal
        const uniqueKey = `${slideIndex}-${threshold.value}`; // Unique key for this threshold and slide
        if (slideProgress >= thresholdValue && !triggeredThresholds.has(uniqueKey)) {
          triggerActions(threshold.actions, "threshold", iframe, indexIframe);
          triggeredThresholds.add(uniqueKey); // Add the unique key
        }
      });
    } else if (scrollDirection === "up") {
      thresholds.sort((a, b) => b.value - a.value); // Sort thresholds from highest to lowest
      thresholds.forEach((threshold) => {
        const thresholdValue = threshold.value / 100; // Convert threshold value to decimal
        const uniqueKey = `${slideIndex}-${threshold.value}`; // Unique key for this threshold and slide
        if (slideProgress <= thresholdValue && !triggeredThresholds.has(uniqueKey)) {
          triggerActions(threshold.actions, "threshold", iframe, indexIframe);
          triggeredThresholds.add(uniqueKey); // Add the unique key
        }
      });
    }
  }
  function handleMessage(event) {
    if (event.data?.type === "svgAspectRatio" && event.data.id === scrollyId) {
      aspectRatioSVG = event.data.value;
    }
  }

  onMount(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  });

  $: if (scrollyContainer) {
    scrollyId;
    aspectRatioSVG;
    updateScrollyMargins();
  }

  $: if (foregroundContainer && externalData) {
    tick().then(() => executeScriptsInContainer(foregroundContainer));
  }
</script>

<svelte:window on:resize={updateScrollyMargins} />

{#if externalData}
  <div
    bind:this={scrollyContainer}
    id={"dv_svelte_scrolly_container_" + scrollyId}
    style:margin-top={scrollyMarginTop}
    style:margin-bottom={scrollyMarginBottom}
  >
    <Scroller {top} {bottom} {threshold} bind:index bind:offset bind:progress bind:count>
      <div slot="background" bind:this={backgroundContainer}>
        <div class="debugging">
          <div>scrollyId: {scrollyId}</div>
          <div>Global: {progress}</div>
          <div>Count: {count}</div>
          <div>Slide: {index} (Offset: {offset ? (offset * 100).toFixed(1) : 0}%)</div>
          <div>Direction: {scrollDirection}</div>
        </div>
        {@html externalData.backgroundContent}
      </div>

      <div slot="foreground" class={scrollyId} bind:this={foregroundContainer}>
        {#each externalData.foregroundContent as sectionContent}
          <section id={sectionContent.onEnter?.length ? sectionContent.onEnter[0]?.args : undefined}>
            <div>
              {#if sectionContent.content}
                {#if sectionContent.type == "textbox"}
                  <div class="scrollybox">
                    {@html DOMPurify.sanitize(sectionContent.content)}
                  </div>
                {/if}
                {#if sectionContent.type == "html"}
                  {@html sectionContent.content}
                {/if}
              {/if}
            </div>
          </section>
        {/each}
      </div>
    </Scroller>
  </div>
{/if}

<style lang="scss">
</style>
